/**
 * useImageDropzone Hook
 *
 * A specialized hook for handling image file drag-and-drop in the Solomon panel.
 * Wraps the shared useFileDropzone hook with image-specific configuration.
 */

import { useState, useCallback, useEffect } from 'react';

import { useFileDropzone, type UseFileDropzoneReturn } from '@pray/shared/hooks/useFileDropzone';

/** Accepted image MIME types and their extensions */
const IMAGE_FILE_TYPES: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

export interface UseImageDropzoneReturn extends UseFileDropzoneReturn {
  /** The dropped image file, if any */
  droppedImage: File | null;
  /** Preview URL for the dropped image (for display purposes) */
  imagePreviewUrl: string | null;
  /** Clear the dropped image and preview URL */
  clearDroppedImage: () => void;
  /** Error message if file was rejected */
  error: string | null;
}

/**
 * Hook for handling image drag-and-drop in the Solomon panel.
 * Creates a preview URL for the dropped image that must be cleaned up.
 *
 * @param disabled - Whether the dropzone is disabled
 * @returns Image dropzone state and handlers
 *
 * @example
 * const {
 *   droppedImage,
 *   imagePreviewUrl,
 *   isDragActive,
 *   clearDroppedImage,
 *   getRootProps,
 *   getInputProps,
 *   error,
 * } = useImageDropzone(isDisabled);
 *
 * // In JSX:
 * <div {...getRootProps()}>
 *   <input {...getInputProps()} />
 *   {imagePreviewUrl && <img src={imagePreviewUrl} />}
 * </div>
 */
export function useImageDropzone(disabled = false): UseImageDropzoneReturn {
  const [droppedImage, setDroppedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Handle accepted file
  const handleAcceptedFiles = useCallback((file: File | File[]) => {
    const imageFile = Array.isArray(file) ? file[0] : file;
    setDroppedImage(imageFile);
    setError(null);

    // Create preview URL for the image
    const url = URL.createObjectURL(imageFile);
    setImagePreviewUrl(url);
  }, []);

  // Handle rejected file
  const handleRejectedFiles = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  // Clear dropped image and revoke preview URL to prevent memory leaks
  const clearDroppedImage = useCallback(() => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setDroppedImage(null);
    setImagePreviewUrl(null);
    setError(null);
  }, [imagePreviewUrl]);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Use the shared file dropzone hook
  const { getRootProps, getInputProps, isDragActive } = useFileDropzone({
    acceptedFileTypes: IMAGE_FILE_TYPES,
    fileTypeDisplay: 'an image (JPG, PNG, or WebP)',
    maxFiles: 1,
    disabled,
    onAcceptedFiles: handleAcceptedFiles,
    onRejectedFiles: handleRejectedFiles,
  });

  return {
    droppedImage,
    imagePreviewUrl,
    isDragActive,
    clearDroppedImage,
    getRootProps,
    getInputProps,
    error,
  };
}

export default useImageDropzone;
